import { useEffect, useState } from "react";
import { Navigate, useParams, useLocation, matchPath } from "react-router-dom";
import { api } from "@/lib/api";
import PausedSite from "@/components/PausedSite";
import "./template-ecommerce/ecommerce-theme.css";
import { TemplateProvider, useTemplate } from "@/context/TemplateContext";
import { CartProvider } from "@/context/CartContext";
import Header from "./template-ecommerce/components/Header";
import Footer from "./template-ecommerce/components/Footer";
import CartDrawer from "./template-ecommerce/components/CartDrawer";

/**
 * Shared shell for every ecommerce page on a published store at
 * `/site/:slug/*`. Each route specifies which page component to render via
 * the `Page` prop (TemplateHome / TemplateShop / TemplateProductDetail /
 * TemplateCheckout / TemplateOrderSuccess).
 *
 * The shell:
 *  1. Fetches `/api/public/site/:slug` once
 *  2. Mounts the SRC-styled ecommerce chrome (Header / Footer / CartDrawer)
 *     inside a TemplateProvider that supplies the backend-built
 *     `ecommerce_config` as overrides and `basePath="/site/<slug>"`.
 *  3. Renders `<Page />` between the chrome.
 *
 * When the site isn't ecommerce (industry_template !== "ecommerce"), redirect
 * to `/site/:slug` so the legacy renderer handles it.
 */
function ShellChrome({ Page, basePath }) {
  const { config } = useTemplate();
  const { pathname } = useLocation();
  // Home = pathname exactly matches the mount point ("/site/:slug" [+/]).
  const isHome = [basePath, `${basePath}/`].some((p) => matchPath({ path: p, end: true }, pathname));
  return (
    <CartProvider pricing={config.pricing}>
      <div className="strvkt-theme min-h-screen flex flex-col">
        <Header
          config={config}
          basePath={basePath}
          transparent={isHome}
          announcementMessages={config?.announcementMessages}
        />
        <main className={`flex-1 ${isHome ? "" : "pt-[104px]"}`}>
          <Page />
        </main>
        <Footer config={config} basePath={basePath} />
        <CartDrawer config={config} basePath={basePath} />
      </div>
    </CartProvider>
  );
}

export default function PublicEcommerceShell({ Page }) {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, err: null });
    (async () => {
      try {
        const { data } = await api.get(`/public/site/${slug}`);
        if (cancelled) return;
        setState({ loading: false, data, err: null });
        const seoTitle = data.ecommerce_config?.seo?.title || data.summary?.name || "Shop";
        document.title = seoTitle;
        const meta = document.querySelector('meta[name="description"]') || (() => {
          const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
        })();
        meta.content = data.ecommerce_config?.seo?.description || data.website_content?.meta_description || "";
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, data: null, err: e?.response?.status === 404 ? "not_found" : "server" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white" data-testid="ecom-shell-loading">Loading…</div>;
  }
  if (state.err === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="public-not-found">
        <div className="nb-card p-8 text-center max-w-md">
          <div className="font-display text-2xl font-extrabold mb-2">Site not found</div>
          <p className="text-gray-600 text-sm">This website may have been moved or unpublished.</p>
        </div>
      </div>
    );
  }
  if (state.err === "server") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="public-server-error">
        <div className="nb-card p-8 text-center max-w-md">
          <div className="font-display text-2xl font-extrabold mb-2">Something went wrong</div>
          <p className="text-gray-600 text-sm">We couldn&apos;t load this site right now. Please try again in a moment.</p>
          <button onClick={() => window.location.reload()} className="inline-block mt-4 text-sm font-semibold underline">Retry</button>
        </div>
      </div>
    );
  }
  if (state.data?.paused) {
    // Hosting inactive → branded platform paused page (no billing details).
    return <PausedSite />;
  }
  if (state.data.industry_template !== "ecommerce") {
    // Not an ecommerce site → let PublicSite route to the right SRC shell.
    return <Navigate to={`/site/${slug}`} replace />;
  }

  const basePath = `/site/${slug}`;
  return (
    <TemplateProvider
      industryKey={null}
      overrides={state.data.ecommerce_config || {}}
      basePath={basePath}
      slug={slug}
      checkoutEnabled={!!state.data.checkout_enabled}
      websiteContent={state.data.website_content || {}}
      summary={state.data.summary || {}}
    >
      <ShellChrome Page={Page} basePath={basePath} />
    </TemplateProvider>
  );
}
